import { Navigate, Route, Routes } from 'react-router-dom';
import { P } from '@stockpilot/shared';
import { AuthGate, TenantGate } from '@/components/AuthGate';
import { RequirePermission } from '@/components/RequirePermission';
import { AppShell } from '@/components/layout/AppShell';
import { EmptyState } from '@/components/ui';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import {
  ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage,
} from '@/pages/auth/PasswordPages';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { DashboardPage } from '@/pages/app/DashboardPage';
import { StockPage } from '@/pages/app/StockPage';
import { ProductsPage } from '@/pages/app/ProductsPage';
import { TransfersPage } from '@/pages/app/TransfersPage';
import { StockCountsPage } from '@/pages/app/StockCountsPage';
import { AdjustmentsPage } from '@/pages/app/AdjustmentsPage';
import { CustomersPage } from '@/pages/app/CustomersPage';
import { SalesPage } from '@/pages/app/SalesPage';
import { PosPage } from '@/pages/app/PosPage';
import { InvoicesPage } from '@/pages/app/InvoicesPage';
import { ReturnsPage } from '@/pages/app/ReturnsPage';
import { PaymentsPage } from '@/pages/app/PaymentsPage';
import { ExpensesPage } from '@/pages/app/ExpensesPage';
import { StatementsPage } from '@/pages/app/StatementsPage';
import { SuppliersPage } from '@/pages/app/SuppliersPage';
import { PurchaseOrdersPage } from '@/pages/app/PurchaseOrdersPage';
import { GoodsReceiptsPage } from '@/pages/app/GoodsReceiptsPage';
import { PayablesPage } from '@/pages/app/PayablesPage';
import { AccountsPage } from '@/pages/app/AccountsPage';
import { ReceivablesPage } from '@/pages/app/ReceivablesPage';
import { SalesReportPage } from '@/pages/app/SalesReportPage';
import { FinancialReportsPage } from '@/pages/app/FinancialReportsPage';
import { PropertySalesPage } from '@/pages/app/PropertySalesPage';
import { StockLedgerPage } from '@/pages/app/StockLedgerPage';
import { WarehousesPage } from '@/pages/app/WarehousesPage';
import { ValuationPage } from '@/pages/app/ValuationPage';
import { ProfilePage } from '@/pages/app/ProfilePage';
import { BranchesPage } from '@/pages/admin/BranchesPage';
import { CatalogPage } from '@/pages/admin/CatalogPage';
import { TeamPage } from '@/pages/admin/TeamPage';
import { RolesPage } from '@/pages/admin/RolesPage';
import { SettingsPage } from '@/pages/admin/SettingsPage';
import { SubscriptionPage } from '@/pages/admin/SubscriptionPage';
import { AuditPage } from '@/pages/admin/AuditPage';

/**
 * Route permissions mirror `src/app/navigation.ts`. A page is reachable only if
 * the member holds the permission — and the API enforces the same rule, so a
 * hand-typed URL gains nothing.
 */
export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app/dashboard" replace />} />

      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/register" element={<RegisterPage />} />
      <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset" element={<ResetPasswordPage />} />
      <Route path="/auth/verify" element={<VerifyEmailPage />} />

      <Route element={<AuthGate />}>
        <Route path="/onboarding" element={<OnboardingPage />} />

        <Route element={<TenantGate />}>
          <Route path="/app" element={<AppShell />}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="profile" element={<ProfilePage />} />

            <Route
              path="inventory/stock"
              element={
                <RequirePermission permissions={[P.STOCK_VIEW]}>
                  <StockPage />
                </RequirePermission>
              }
            />
            <Route
              path="pos"
              element={
                <RequirePermission permissions={[P.POS_ACCESS]}>
                  <PosPage />
                </RequirePermission>
              }
            />
            <Route
              path="sales"
              element={
                <RequirePermission permissions={[P.SALE_VIEW]}>
                  <SalesPage />
                </RequirePermission>
              }
            />
            <Route
              path="sales/quotations"
              element={
                <RequirePermission permissions={[P.SALE_VIEW]}>
                  <SalesPage defaultType="QUOTATION" />
                </RequirePermission>
              }
            />
            <Route
              path="purchases/suppliers"
              element={
                <RequirePermission permissions={[P.SUPPLIER_VIEW]}>
                  <SuppliersPage />
                </RequirePermission>
              }
            />
            <Route
              path="purchases/orders"
              element={
                <RequirePermission permissions={[P.PURCHASE_VIEW]}>
                  <PurchaseOrdersPage />
                </RequirePermission>
              }
            />
            <Route
              path="purchases/receipts"
              element={
                <RequirePermission permissions={[P.PURCHASE_RECEIVE]}>
                  <GoodsReceiptsPage />
                </RequirePermission>
              }
            />
            <Route
              path="sales/property"
              element={
                <RequirePermission permissions={[P.PROPERTY_VIEW]}>
                  <PropertySalesPage />
                </RequirePermission>
              }
            />
            <Route
              path="reports/sales"
              element={
                <RequirePermission permissions={[P.REPORT_VIEW]}>
                  <SalesReportPage />
                </RequirePermission>
              }
            />
            <Route
              path="finance/receivables"
              element={
                <RequirePermission permissions={[P.RECEIVABLE_VIEW]}>
                  <ReceivablesPage />
                </RequirePermission>
              }
            />
            <Route
              path="finance/accounts"
              element={
                <RequirePermission permissions={[P.PAYMENT_VIEW]}>
                  <AccountsPage />
                </RequirePermission>
              }
            />
            <Route
              path="finance/payables"
              element={
                <RequirePermission permissions={[P.PAYABLE_VIEW]}>
                  <PayablesPage />
                </RequirePermission>
              }
            />
            <Route
              path="finance/expenses"
              element={
                <RequirePermission permissions={[P.EXPENSE_VIEW]}>
                  <ExpensesPage />
                </RequirePermission>
              }
            />
            <Route
              path="customers/statements"
              element={
                <RequirePermission permissions={[P.RECEIVABLE_VIEW]}>
                  <StatementsPage />
                </RequirePermission>
              }
            />
            <Route
              path="finance/payments"
              element={
                <RequirePermission permissions={[P.PAYMENT_VIEW]}>
                  <PaymentsPage />
                </RequirePermission>
              }
            />
            <Route
              path="sales/invoices"
              element={
                <RequirePermission permissions={[P.INVOICE_VIEW]}>
                  <InvoicesPage />
                </RequirePermission>
              }
            />
            <Route
              path="sales/returns"
              element={
                <RequirePermission permissions={[P.SALE_REFUND]}>
                  <ReturnsPage />
                </RequirePermission>
              }
            />
            <Route
              path="customers"
              element={
                <RequirePermission permissions={[P.CUSTOMER_VIEW]}>
                  <CustomersPage />
                </RequirePermission>
              }
            />
            <Route
              path="inventory/adjustments"
              element={
                <RequirePermission permissions={[P.STOCK_VIEW]}>
                  <AdjustmentsPage />
                </RequirePermission>
              }
            />
            <Route
              path="inventory/products"
              element={
                <RequirePermission permissions={[P.PRODUCT_VIEW]}>
                  <ProductsPage />
                </RequirePermission>
              }
            />
            <Route
              path="inventory/transfers"
              element={
                <RequirePermission permissions={[P.STOCK_TRANSFER]}>
                  <TransfersPage />
                </RequirePermission>
              }
            />
            <Route
              path="inventory/counts"
              element={
                <RequirePermission permissions={[P.STOCK_COUNT]}>
                  <StockCountsPage />
                </RequirePermission>
              }
            />
            <Route
              path="inventory/ledger"
              element={
                <RequirePermission permissions={[P.STOCK_VIEW]}>
                  <StockLedgerPage />
                </RequirePermission>
              }
            />
            <Route
              path="inventory/warehouses"
              element={
                <RequirePermission permissions={[P.WAREHOUSE_VIEW]}>
                  <WarehousesPage />
                </RequirePermission>
              }
            />
            <Route
              path="reports/valuation"
              element={
                <RequirePermission permissions={[P.PRODUCT_COST_VIEW]}>
                  <ValuationPage />
                </RequirePermission>
              }
            />

            <Route
              path="admin/team"
              element={
                <RequirePermission permissions={[P.USER_VIEW]}>
                  <TeamPage />
                </RequirePermission>
              }
            />
            <Route
              path="admin/roles"
              element={
                <RequirePermission permissions={[P.ROLE_VIEW]}>
                  <RolesPage />
                </RequirePermission>
              }
            />
            <Route
              path="admin/branches"
              element={
                <RequirePermission permissions={[P.BRANCH_VIEW]}>
                  <BranchesPage />
                </RequirePermission>
              }
            />
            <Route
              path="admin/catalog"
              element={
                <RequirePermission permissions={[P.PRODUCT_VIEW]}>
                  <CatalogPage />
                </RequirePermission>
              }
            />
            <Route
              path="reports/finance"
              element={
                <RequirePermission permissions={[P.REPORT_FINANCIAL_VIEW]}>
                  <FinancialReportsPage />
                </RequirePermission>
              }
            />
            <Route
              path="admin/settings"
              element={
                <RequirePermission permissions={[P.ORGANIZATION_VIEW]}>
                  <SettingsPage />
                </RequirePermission>
              }
            />
            <Route
              path="admin/subscription"
              element={
                <RequirePermission permissions={[P.ORGANIZATION_VIEW]}>
                  <SubscriptionPage />
                </RequirePermission>
              }
            />
            <Route
              path="admin/audit"
              element={
                <RequirePermission permissions={[P.AUDIT_VIEW]}>
                  <AuditPage />
                </RequirePermission>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
    </Routes>
  );
}

function NotFound() {
  return (
    <EmptyState
      title="This page does not exist yet"
      description="It may belong to a module scheduled for a later phase, or the link may be out of date."
    />
  );
}
